import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Dialog, DialogActions, DialogBody, DialogTitle} from "catalyst";
import {DiagnosticList} from "./DiagnosticList";
import {SEVERITY_ORDER} from "./types";
import type {DiagnosticRun, DiagnosticSeverity, ObjectRef} from "./types";

export type DiagnosticsDialogProps = {
  run?: DiagnosticRun;
  open?: boolean;
  onClose?: () => void;
  /** Whether this screen can reach the object a diagnostic points at. */
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
  /**
   * Unstructured text from a caller that has not moved to diagnostics yet, shown under its own
   * heading. Transitional: report validation still returns one newline-joined blob.
   */
  additionalText?: string;
  title?: string;
  /**
   * Whether this run is currently kept in the status bar. Controls which way the keep/remove
   * control reads; omit the handlers below to hide it entirely.
   */
  pinned?: boolean;
  onPin?: () => void;
  onUnpin?: () => void;
};

const severityNoun: Record<DiagnosticSeverity, string> = {
  CRITICAL: "critical",
  ERROR: "error",
  WARNING: "warning",
  INFO: "note"
};

const badgeClasses: Record<string, string> = {
  CRITICAL: "bg-ots-red-100 text-ots-red-900 dark:bg-ots-red-950 dark:text-ots-red-200",
  ERROR: "bg-ots-red-100 text-ots-red-800 dark:bg-ots-red-950 dark:text-ots-red-300",
  WARNING: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  INFO: "bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200"
};

/**
 * What a run had to say about itself.
 *
 * Leads with the worst severity present, because that is what decides whether the reader needs to
 * act at all before reading any further.
 */
export const DiagnosticsDialog = ({
  run,
  open = true,
  onClose,
  canNavigate,
  onNavigate,
  additionalText,
  title,
  pinned = false,
  onPin,
  onUnpin
}: DiagnosticsDialogProps) => {
  // Usable both standalone -- GWT mounts it and walks away -- and controlled. Own state drives the
  // rendering either way, so the dialog can play its close transition without waiting for the host
  // to hand back a new `open`; `onClose` then tells the host to tear the mount down.
  const [internalOpen, setInternalOpen] = useState(open);

  useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  const contentRef = useRef<HTMLDivElement>(null);
  const closedRef = useRef(false);

  const close = useCallback(() => {
    // Both Headless UI and the outside-click listener below can fire for one dismissal; the host
    // should only be told once, because it tears the mount down in response.
    if (closedRef.current) {
      return;
    }
    closedRef.current = true;

    setInternalOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!internalOpen) {
      return;
    }

    closedRef.current = false;

    // Headless UI's own outside-click detection compares against the composed tree it can see, and
    // this dialog is portaled into a shadow-DOM overlay when GWT mounts it -- so a click on the
    // backdrop looks like it landed on the shadow host, i.e. "inside", and never closes.
    // composedPath() crosses that boundary, so it can answer the question correctly.
    const onPointerDown = (event: Event) => {
      const panel = contentRef.current?.closest('[role="dialog"]');
      if (!panel) {
        return;
      }

      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      if (!path.includes(panel)) {
        close();
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [internalOpen, close]);

  const counts = useMemo(() => {
    return SEVERITY_ORDER
      .map((severity) => ({severity, count: run?.counts?.[severity] ?? 0}))
      .filter((entry) => entry.count > 0);
  }, [run]);

  const heading = title ?? headingFor(run?.worstSeverity);

  return (
    <Dialog open={internalOpen} onClose={close} size="3xl">
      <DialogTitle>{heading}</DialogTitle>

      <DialogBody>
        <div ref={contentRef}>
        {counts.length ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {counts.map(({severity, count}) => (
              <span
                key={severity}
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClasses[severity]}`}>
                {count} {severityNoun[severity]}{count === 1 ? "" : "s"}
              </span>
            ))}
            {run?.suppressedCount ? (
              <span className="text-xs text-brand-600 dark:text-brand-300">
                {run.suppressedCount} not shown
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="max-h-[60vh] overflow-y-auto">
          <DiagnosticList
            diagnostics={run?.diagnostics}
            canNavigate={canNavigate}
            onNavigate={onNavigate && ((ref) => {
              // Stand down before handing over: the row being navigated to is behind this dialog.
              close();
              onNavigate(ref);
            })}/>

          {additionalText?.trim() ? (
            <div className="mt-4">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-brand-700 dark:text-brand-300">
                Validation
              </div>
              <pre className="whitespace-pre-wrap rounded-md border border-brand-200 bg-brand-50 p-3 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-900 dark:text-brand-200">
                {additionalText.trim()}
              </pre>
            </div>
          ) : null}
        </div>
        </div>
      </DialogBody>

      <DialogActions>
        {/* Keeping the run is deliberate rather than automatic: most runs are read once and
            dismissed, and a status item nobody asked for is clutter. */}
        {onPin || onUnpin ? (
          <Button
            plain
            style={{cursor: "pointer"}}
            onClick={() => {
              // Deciding what happens to the run is the last thing the reader wants from this
              // dialog, so it stands down afterwards rather than making them close it too.
              if (pinned) {
                onUnpin?.();
              } else {
                onPin?.();
              }
              close();
            }}>
            {pinned ? "Remove from status bar" : "Keep in status bar"}
          </Button>
        ) : null}

        {/* Catalyst's Button sets cursor-default; an inline style is the one thing that reliably
            wins over it regardless of stylesheet ordering. */}
        <Button style={{cursor: "pointer"}} onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const headingFor = (worstSeverity?: string): string => {
  if (worstSeverity === "CRITICAL" || worstSeverity === "ERROR") {
    return "Report Errors";
  }
  if (worstSeverity === "WARNING") {
    return "Report Warnings";
  }
  return "Report Messages";
};
